"use client";

import { useId, useRef, useState } from "react";
import Link from "next/link";
import {
  ArrowRight,
  Check,
  Infinity as InfinityIcon,
  RefreshCw,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { CtaLink } from "@/components/CtaLink";
import { STUDIO_URL } from "@/lib/urls";

const EASE_OUT = [0.22, 1, 0.36, 1] as const;
const STUDIO_SIGNUP_URL = `${STUDIO_URL}/signup`;

/** Flat lemon, same as the yellow pills and buttons across the site. */
export const GOLD_FILL = "#ffe500";

export type Billing = "monthly" | "yearly";
/** USD only (Joe, 15-Oct). Kept as a type so price lookups stay keyed. */
export type Currency = "USD";
export type Audience = "solo" | "team";

export type Plan = {
  id: string;
  audience: Audience;
  name: string;
  tagline: string;
  price: Record<Currency, Record<Billing, string>>;
  unit: string;
  note: Record<Billing, string>;
  lead?: string;
  features: string[];
  featured?: boolean;
  cta: { label: string; href?: string };
};

/** Checklist lines on Pro (mirrors the in-app upgrade dialog). Plus and Max
    say "Everything in Pro" instead of repeating them, so each card shows only
    what changes: credits and how many embeds and stands are included. */
const PRO_FEATURES = [
  "All AI models (inc. frontier)",
  "Memory across chats",
  "Chief of Staff (daily briefings, meeting transcripts, email & calendar management)",
  "Generous custom agent allowance",
  "Searchable audit trail",
  "SOC 2 Type I (in progress)",
  "GDPR compliant",
  "Analytics",
  "Unused credits roll over for one month",
];

const TEAM_NOTE = {
  monthly: "Minimum 5 seats. Billed monthly.",
  yearly: "Minimum 5 seats. Team is billed monthly only.",
};

/** Team Plus and Team Max are priced before VAT (Stripe prices are VAT-exclusive). */
const TEAM_NOTE_PLUS_VAT = {
  monthly: "Plus VAT. Minimum 5 seats. Billed monthly.",
  yearly: "Plus VAT. Minimum 5 seats. Team is billed monthly only.",
};

export const PLANS: Plan[] = [
  {
    id: "free",
    audience: "solo",
    name: "Free",
    tagline: "Get started at your own pace, no card required.",
    price: { USD: { monthly: "$0", yearly: "$0" } },
    unit: "/month",
    note: { monthly: "Free forever.", yearly: "Free forever." },
    features: [
      "2,000 credits once, no monthly reset",
      "Every AI model",
      "Build AI agents for free",
      "Memory & connected apps built in",
      "Document & image generation",
    ],
    cta: { label: "Start free", href: STUDIO_SIGNUP_URL },
  },
  {
    id: "pro",
    audience: "solo",
    name: "Pro",
    tagline: "For one person using AI every day.",
    price: { USD: { monthly: "$49", yearly: "$39" } },
    unit: "/month",
    note: {
      monthly: "Billed monthly. Cancel any time.",
      yearly: "{total} billed annually. Save 20%.",
    },
    featured: true,
    features: [
      "10,000 credits a month",
      "1 embed and 1 stand included",
      ...PRO_FEATURES,
    ],
    cta: { label: "Choose Pro", href: STUDIO_SIGNUP_URL },
  },
  {
    id: "plus",
    audience: "solo",
    name: "Plus",
    tagline: "For people who run part of their working week through it.",
    price: { USD: { monthly: "$99", yearly: "$79" } },
    unit: "/month",
    note: {
      monthly: "Billed monthly. Cancel any time.",
      yearly: "{total} billed annually. Save 20%.",
    },
    lead: "Everything in Pro, plus:",
    features: [
      "22,500 credits a month, about 2× Pro",
      "3 embeds and 3 stands included",
    ],
    cta: { label: "Choose Plus", href: STUDIO_SIGNUP_URL },
  },
  {
    id: "max",
    audience: "solo",
    name: "Max",
    tagline: "For heavy daily use across clients and projects.",
    price: { USD: { monthly: "$199", yearly: "$159" } },
    unit: "/month",
    note: {
      monthly: "Billed monthly. Cancel any time.",
      yearly: "{total} billed annually. Save 20%.",
    },
    lead: "Everything in Pro, plus:",
    features: [
      "50,000 credits a month, 5× Pro",
      "10 embeds and 10 stands included",
    ],
    cta: { label: "Choose Max", href: STUDIO_SIGNUP_URL },
  },
  {
    id: "team",
    audience: "team",
    name: "Team Pro",
    tagline: "Roll it out across the firm, governed.",
    price: { USD: { monthly: "$39", yearly: "$39" } },
    unit: "/seat/month",
    note: TEAM_NOTE,
    featured: true,
    lead: "Everything in Pro for every seat, plus:",
    features: [
      "10,000 credits a month per seat, in one shared pool",
      "3 embeds and 3 stands included",
      "Admin, manager and member roles, one bill for the team",
      "Model controls: allow or block providers, require zero data retention, every change logged",
      "Team-wide guardrails that apply to every chat",
      "Approvals before an assistant is shared with the team",
      "Team access log",
    ],
    cta: { label: "Choose Team Pro", href: STUDIO_SIGNUP_URL },
  },
  {
    id: "team-plus",
    audience: "team",
    name: "Team Plus",
    tagline: "For teams that work in it every day.",
    price: { USD: { monthly: "$89", yearly: "$89" } },
    unit: "/seat/month",
    note: TEAM_NOTE_PLUS_VAT,
    lead: "Everything in Team Pro, plus:",
    features: [
      "22,500 credits a month per seat, about 2× Team Pro",
      "5 embeds and 5 stands included",
    ],
    cta: { label: "Choose Team Plus", href: STUDIO_SIGNUP_URL },
  },
  {
    id: "team-max",
    audience: "team",
    name: "Team Max",
    tagline: "For teams running heavy workloads.",
    price: { USD: { monthly: "$189", yearly: "$189" } },
    unit: "/seat/month",
    note: TEAM_NOTE_PLUS_VAT,
    lead: "Everything in Team Pro, plus:",
    features: [
      "50,000 credits a month per seat, 5× Team Pro",
      "10 embeds and 10 stands included",
    ],
    cta: { label: "Choose Team Max", href: STUDIO_SIGNUP_URL },
  },
  {
    id: "enterprise",
    audience: "team",
    name: "Enterprise",
    tagline: "Scoped and governed around your firm.",
    price: { USD: { monthly: "Custom", yearly: "Custom" } },
    unit: "",
    note: {
      monthly: "Scoped per firm on a call.",
      yearly: "Scoped per firm on a call.",
    },
    lead: "Everything in Team Max, plus:",
    features: [
      "Dedicated account manager",
      "Custom integrations",
      "SSO & advanced security",
      "SLA & uptime guarantees",
      "Credit allowance sized to your firm",
    ],
    cta: { label: "Speak with us", href: "/book" },
  },
];

/** Full-year charge for a plan, derived from its per-month price at that billing period. */
export function yearTotal(plan: Plan, currency: Currency, billing: Billing) {
  const price = plan.price[currency][billing];
  const amount = Number.parseInt(price.slice(1), 10);
  return Number.isNaN(amount) ? null : `${price[0]}${amount * 12}`;
}

export function annualSavingsCaption(currency: Currency, billing: Billing) {
  const pro = PLANS.find((p) => p.id === "pro")!;
  const monthly = yearTotal(pro, currency, "monthly");
  const yearly = yearTotal(pro, currency, "yearly");
  return billing === "yearly"
    ? `Pro billed annually: ${yearly}/year instead of ${monthly}`
    : `Pay annually and Pro saves 20%: ${yearly}/year instead of ${monthly}`;
}

/** What a year of Pro saves when billed annually, e.g. "$120". */
export function annualSaving(currency: Currency) {
  const pro = PLANS.find((p) => p.id === "pro")!;
  const monthly = Number.parseInt(pro.price[currency].monthly.slice(1), 10);
  const yearly = Number.parseInt(pro.price[currency].yearly.slice(1), 10);
  return `${pro.price[currency].monthly[0]}${(monthly - yearly) * 12}`;
}

export function plansFor(audience: Audience) {
  return PLANS.filter((p) => p.audience === audience);
}

/** Two-up for Team, four-up for Solo so Pro, Plus and Max sit beside Free. */
export function planGridClass(count: number) {
  return count > 2
    ? "max-w-[1320px] sm:grid-cols-2 xl:grid-cols-4"
    : "max-w-[920px] sm:grid-cols-2";
}

export const AUDIENCES: { id: Audience; label: string }[] = [
  { id: "solo", label: "Solo" },
  { id: "team", label: "Team" },
];

export const EVERY_PLAN = [
  { icon: InfinityIcon, label: "A generous agent allowance on every tier" },
  { icon: RefreshCw, label: "Upgrade or downgrade any time" },
  { icon: ShieldCheck, label: "Permission controls built in" },
  { icon: XCircle, label: "Cancel whenever you like" },
];

export function FeatureItem({
  children,
  onPlate = false,
}: {
  children: React.ReactNode;
  onPlate?: boolean;
}) {
  return (
    <li
      className={`flex items-start gap-2.5 text-[13.5px] leading-relaxed ${
        onPlate ? "text-on-plate/75" : "text-on-dark/75"
      }`}
    >
      <Check
        size={14}
        strokeWidth={2.5}
        className="mt-[4px] shrink-0"
        style={{
          color: onPlate ? "hsl(142 70% 58%)" : "hsl(var(--green-ink))",
        }}
        aria-hidden="true"
      />
      <span>{children}</span>
    </li>
  );
}

export function BillingToggle({
  billing,
  onChange,
  layoutId = "billing-pill",
}: {
  billing: Billing;
  onChange: (b: Billing) => void;
  layoutId?: string;
}) {
  const options: { id: Billing; label: string }[] = [
    { id: "monthly", label: "Monthly" },
    { id: "yearly", label: "Annual" },
  ];

  return (
    <div
      role="group"
      aria-label="Billing period"
      className="inline-flex items-center gap-1.5 rounded-full border border-hairline-strong bg-panel-solid p-1.5"
    >
      {options.map((option) => {
        const active = billing === option.id;
        return (
          <button
            key={option.id}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.id)}
            className="relative rounded-full px-9 py-3.5 font-heading text-[19px] font-semibold transition-colors"
            style={{
              color: active ? "var(--primary-foreground)" : undefined,
            }}
          >
            {active && (
              <motion.span
                layoutId={layoutId}
                aria-hidden="true"
                className="absolute inset-0 rounded-full"
                style={{
                  background: GOLD_FILL,
                }}
                transition={{ type: "spring", stiffness: 420, damping: 36 }}
              />
            )}
            <span
              className={`relative z-10 ${
                active ? "" : "text-on-dark/55 hover:text-on-dark"
              }`}
            >
              {option.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Compact Monthly / Yearly toggle; only the solo plans have a yearly price. */
export function BillingSegment({
  billing,
  onChange,
}: {
  billing: Billing;
  onChange: (b: Billing) => void;
}) {
  const layoutId = useId();
  const options: { id: Billing; label: string }[] = [
    { id: "monthly", label: "Monthly" },
    { id: "yearly", label: "Yearly" },
  ];

  return (
    <div
      role="group"
      aria-label="Billing period"
      className="inline-flex items-center gap-1 rounded-full border border-hairline-strong bg-panel-solid p-1 font-heading text-[13px] font-semibold normal-case tracking-normal"
    >
      {options.map((option) => {
        const active = billing === option.id;
        return (
          <button
            key={option.id}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.id)}
            className="relative inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--gold-ink))]"
            style={{
              color: active ? "var(--primary-foreground)" : undefined,
            }}
          >
            {active && (
              <motion.span
                layoutId={layoutId}
                aria-hidden="true"
                className="absolute inset-0 rounded-full"
                style={{ background: GOLD_FILL }}
                transition={{ type: "spring", stiffness: 420, damping: 36 }}
              />
            )}
            <span
              className={`relative z-10 ${
                active ? "" : "text-on-dark/55 hover:text-on-dark"
              }`}
            >
              {option.label}
            </span>
            {option.id === "yearly" && (
              <span
                className="relative z-10 rounded-full px-2 py-0.5 text-[11px] font-bold"
                style={
                  active
                    ? { background: "#fff", color: "hsl(145 62% 28%)" }
                    : {
                        background: "hsl(var(--green-ink) / 0.16)",
                        color: "hsl(var(--green-ink))",
                      }
                }
              >
                Save 20%
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Billing toggle under the Solo/Team tabs. Only Solo has a yearly price, so Team shows nothing. */
export function PlanOptions({
  audience,
  billing,
  onBillingChange,
}: {
  audience: Audience;
  billing: Billing;
  onBillingChange: (b: Billing) => void;
}) {
  if (audience !== "solo") return null;
  return (
    <div className="flex justify-center">
      <BillingSegment billing={billing} onChange={onBillingChange} />
    </div>
  );
}

export function AudienceTabs({
  audience,
  onChange,
  panelId,
  layoutId = "audience-pill",
}: {
  audience: Audience;
  onChange: (a: Audience) => void;
  panelId: string;
  layoutId?: string;
}) {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  function onKeyDown(e: React.KeyboardEvent, index: number) {
    const last = AUDIENCES.length - 1;
    let next: number;
    if (e.key === "ArrowRight") next = index === last ? 0 : index + 1;
    else if (e.key === "ArrowLeft") next = index === 0 ? last : index - 1;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = last;
    else return;
    e.preventDefault();
    onChange(AUDIENCES[next].id);
    tabRefs.current[next]?.focus();
  }

  return (
    <div
      role="tablist"
      aria-label="Plan type"
      className="inline-flex items-center gap-1 rounded-full border border-hairline-strong bg-panel-solid p-1"
    >
      {AUDIENCES.map((option, i) => {
        const active = audience === option.id;
        return (
          <button
            key={option.id}
            ref={(el) => {
              tabRefs.current[i] = el;
            }}
            id={`${panelId}-tab-${option.id}`}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={panelId}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(option.id)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className="relative rounded-full px-6 py-2 font-heading text-[15px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--gold-ink))]"
            style={{
              color: active ? "var(--primary-foreground)" : undefined,
            }}
          >
            {active && (
              <motion.span
                layoutId={layoutId}
                aria-hidden="true"
                className="absolute inset-0 rounded-full"
                style={{
                  background: GOLD_FILL,
                }}
                transition={{ type: "spring", stiffness: 420, damping: 36 }}
              />
            )}
            <span
              className={`relative z-10 ${
                active ? "" : "text-on-dark/55 hover:text-on-dark"
              }`}
            >
              {option.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function PlanCard({
  plan,
  billing,
  currency,
  index,
  reduce,
  ctaLocation = "pricing-plan",
}: {
  plan: Plan;
  billing: Billing;
  currency: Currency;
  index: number;
  reduce: boolean;
  ctaLocation?: string;
}) {
  const featured = !!plan.featured;
  const price = plan.price[currency][billing];
  const ctaLabel = plan.cta.label;
  const ctaClass = featured
    ? "btn-gold w-full py-3 text-[14px]"
    : "btn-plan w-full";

  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, ease: EASE_OUT, delay: index * 0.07 }}
      className={`plan-card p-7 md:p-8 ${featured ? "plan-card-featured" : ""}`}
    >
      {featured && (
        <span
          className="absolute -top-3 left-7 rounded-full px-3 py-1 font-accent text-[10px] font-bold uppercase tracking-[0.14em]"
          style={{
            background: GOLD_FILL,
            color: "var(--primary-foreground)",
          }}
        >
          Most popular
        </span>
      )}

      <h3
        className="font-heading text-[22px] tracking-[-0.02em] text-on-dark"
        style={{ fontWeight: 560 }}
      >
        {plan.name}
      </h3>
      <p
        className="mt-2 min-h-[2.6rem] text-[13px] leading-snug text-on-dark/55"
      >
        {plan.tagline}
      </p>

      <div className="mt-5 flex flex-wrap items-baseline gap-1.5">
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.span
            key={price}
            initial={reduce ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? undefined : { opacity: 0, y: -14 }}
            transition={{ duration: 0.28, ease: EASE_OUT }}
            className="heading-pure font-heading leading-none"
            style={{
              fontSize: price.length > 4 ? "2.35rem" : "3.1rem",
              fontWeight: 480,
              letterSpacing: "-0.045em",
            }}
          >
            {price}
          </motion.span>
        </AnimatePresence>
        {plan.unit && (
          <span
            className={`text-[13px] text-on-dark/50`}
          >
            {plan.unit}
          </span>
        )}
      </div>
      <p
        className={`mt-2 text-[15px] text-on-dark/45`}
      >
        {plan.note[billing].replace(
          "{total}",
          yearTotal(plan, currency, billing) ?? "",
        )}
      </p>

      <div
        className="my-6 h-px w-full"
        style={{
          background: "var(--hairline)",
        }}
        aria-hidden="true"
      />

      {plan.lead && (
        <p
          className={`mb-3 text-[12px] font-medium text-on-dark/60`}
        >
          {plan.lead}
        </p>
      )}

      <ul className="flex flex-1 flex-col gap-2.5">
        {plan.features.map((feature) => (
          <FeatureItem key={feature}>
            {feature}
          </FeatureItem>
        ))}
      </ul>

      <div className="mt-8">
        {plan.cta.href?.startsWith("/") ? (
          <Link href={plan.cta.href} className={ctaClass}>
            {ctaLabel}
            <ArrowRight size={15} strokeWidth={2} />
          </Link>
        ) : (
          <CtaLink
            location={ctaLocation}
            label={ctaLabel}
            href={plan.cta.href}
            trackLead
            className={ctaClass}
          >
            {ctaLabel}
            <ArrowRight size={15} strokeWidth={2} />
          </CtaLink>
        )}
      </div>
    </motion.div>
  );
}

export default function PricingPlans({
  ctaLocation = "pricing-plan",
  showControls = true,
  showGuarantees = true,
  layoutId = "billing-pill",
  defaultAudience = "solo",
  className = "",
}: {
  ctaLocation?: string;
  showControls?: boolean;
  showGuarantees?: boolean;
  layoutId?: string;
  defaultAudience?: Audience;
  className?: string;
}) {
  const [audience, setAudience] = useState<Audience>(defaultAudience);
  const [billing, setBilling] = useState<Billing>("monthly");
  const currency: Currency = "USD";
  const reduce = !!useReducedMotion();
  const panelId = useId();

  return (
    <div className={className}>
      <div className="flex flex-col items-center justify-center gap-5">
        <AudienceTabs
          audience={audience}
          onChange={setAudience}
          panelId={panelId}
          layoutId={`${layoutId}-audience`}
        />
        {showControls && (
          <PlanOptions
            audience={audience}
            billing={billing}
            onBillingChange={setBilling}
          />
        )}
      </div>

      <div
        id={panelId}
        role="tabpanel"
        aria-labelledby={`${panelId}-tab-${audience}`}
        className={`mx-auto grid grid-cols-1 items-stretch gap-5 pt-4 text-left ${planGridClass(
          plansFor(audience).length,
        )} ${showControls ? "mt-12" : "mt-8"}`}
      >
        {plansFor(audience).map((plan, i) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            billing={billing}
            currency={currency}
            index={i}
            reduce={reduce}
            ctaLocation={ctaLocation}
          />
        ))}
      </div>

      {showGuarantees && (
        <motion.ul
          className="mx-auto mt-10 flex max-w-[1180px] flex-wrap items-center justify-center gap-x-10 gap-y-4 rounded-[22px] border border-hairline px-7 py-6 text-left"
          initial={reduce ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: EASE_OUT }}
        >
          {EVERY_PLAN.map(({ icon: Icon, label }) => (
            <li
              key={label}
              className="flex items-center gap-2.5 whitespace-nowrap text-[13px] text-on-dark/70"
            >
              <Icon
                size={15}
                strokeWidth={2}
                className="shrink-0"
                style={{ color: "hsl(var(--gold-ink))" }}
                aria-hidden="true"
              />
              {label}
            </li>
          ))}
        </motion.ul>
      )}
    </div>
  );
}
